/**
 * UUID validation utilities
 * Prevents accidental parsing of UUIDs as numbers, which would truncate them
 */

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Validates that a string is a valid UUID v1-5
 */
export function isValidUUID(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }
  return UUID_REGEX.test(value);
}

/**
 * Asserts that a value is a valid UUID string.
 * Throws an error if the value is not a valid UUID.
 * This is useful for catching bugs where UUIDs might be accidentally
 * converted to numbers (which truncates them).
 */
export function assertUUID(value: unknown, fieldName = 'id'): string {
  if (!isValidUUID(value)) {
    throw new Error(
      `Invalid UUID for ${fieldName}: "${value}". ` +
        `Expected a UUID string (e.g., "123e4567-e89b-12d3-a456-426614174000"), ` +
        `but got ${typeof value}. ` +
        `This may indicate that a UUID was accidentally parsed as a number.`,
    );
  }
  return value;
}

/**
 * Validates a UUID and returns it, or returns undefined if invalid.
 * Useful for optional UUID fields.
 */
export function validateUUID(value: unknown): string | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (!isValidUUID(value)) {
    console.warn(
      `Invalid UUID received: "${value}". This may indicate a bug where a UUID was parsed as a number.`,
    );
    return undefined;
  }
  return value;
}
